import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import BelvoirExplorer from './BelvoirExplorer';

/**
 * Interactive Belvoir Castle gardens map.
 * Loads the Blender-exported GLB (belvoir-gardens.glb) built from
 * belvoir-castle-gardens.blend — not a procedural rebuild.
 *
 * Sits below the fixed site header (TopNav + Navbar + announcement banner).
 */
export default function CastleMapPage() {
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const prevOverflow = body.style.overflow;

    const syncHeaderHeight = () => {
      const header = document.getElementById('site-header');
      if (!header) {
        root.style.setProperty('--site-header-height', '160px');
        return;
      }

      // Use the bottom of the last real header bar (top nav, main nav, announcement),
      // not the hanging Halloween tag or reserved padding.
      const bars = [...header.children].filter((el) => {
        const style = getComputedStyle(el);
        if (style.position === 'absolute') return false;
        if (style.display === 'none' || style.visibility === 'hidden') return false;
        return el.getBoundingClientRect().height > 8;
      });
      const lastBar = bars[bars.length - 1] || header;
      const top = Math.round(lastBar.getBoundingClientRect().bottom);
      root.style.setProperty('--site-header-height', `${Math.max(top, 1)}px`);
    };

    syncHeaderHeight();
    body.style.overflow = 'hidden';

    const header = document.getElementById('site-header');
    const observer =
      header && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(syncHeaderHeight)
        : null;
    if (header && observer) observer.observe(header);
    window.addEventListener('resize', syncHeaderHeight);

    return () => {
      body.style.overflow = prevOverflow;
      window.removeEventListener('resize', syncHeaderHeight);
      observer?.disconnect();
      root.style.removeProperty('--site-header-height');
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Explore the Gardens — 3D Map | Belvoir Castle</title>
        <meta
          name="description"
          content="Explore Belvoir Castle and gardens in an interactive 3D map built from the estate’s Blender model. Rotate, zoom, and discover places across the grounds."
        />
      </Helmet>
      <BelvoirExplorer assetBase="" embedded />
    </>
  );
}
